/* eslint-disable @typescript-eslint/naming-convention */
import { Component, forwardRef, Inject, OnInit, ChangeDetectionStrategy } from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { MatIconModule } from '@angular/material/icon';
import { DTypeForm } from '@grupo-constant/next-dtype/form';
import { CodebarScannerWithSelect, DType, SelectFromUri, SelectWithGivenOptions } from '@grupo-constant/next-dtype/types';
import { TranslocoPipe, TranslocoService } from '@ngneat/transloco';
import { Subject, takeUntil } from 'rxjs';
import { DtypeComponentListComponent } from '../../dtype-component-list/dtype-component-list.component';
import { DTypeService } from '../../dtype.service';
import { LocaleString } from '../../../translate/localeString.model';
import { DialogsService } from '../../../../dialogs/dialogs.service';
import { FuseConfirmationService } from '../../../../../@fuse/services/confirmation/confirmation.service';
import { FuseConfirmationDialogComponent } from '../../../../../@fuse/services/confirmation/dialog/dialog.component';

@Component({
    selector: 'app-table-row-dialog',
    templateUrl: './table-row.dialog.component.html',
    standalone: true,
    imports: [forwardRef(() => DtypeComponentListComponent), MatButtonModule, MatIconModule, TranslocoPipe],
    changeDetection: ChangeDetectionStrategy.Eager,
    styles: [
        /* language=SCSS */
        `
            .table-row-dialog-panel {

                .mat-mdc-dialog-container {

                    .mat-mdc-dialog-surface {
                        padding: 0 !important;
                    }
                }
            }
        `
    ],
})
export class TableRowDialogComponent implements OnInit {

    form: DTypeForm;

    private unsubscribe = new Subject<void>();

    constructor(
        @Inject(MAT_DIALOG_DATA) public data: {
            dtype: DType;
            values?: any;
            fixedUniqueField?: string;
            title?: LocaleString;
            isUpdate: boolean;
        },
        public dialogRef: MatDialogRef<TableRowDialogComponent>,
        private dialogsService: DialogsService,
        private dtypeService: DTypeService,
        private _fuseConfirmationService: FuseConfirmationService,
        private transloco: TranslocoService
    ) { }

    async ngOnInit() {
        this.form = this.dtypeService.getDtypeFormFromDtype(this.data.dtype, this.data.values, { isUpdate: this.data.isUpdate });
        if (this.data.fixedUniqueField) {
            const selectField = this.data.dtype.column.find(c => c.name === this.data.fixedUniqueField) as SelectWithGivenOptions | SelectFromUri | CodebarScannerWithSelect;
            const c = this.form.control.get(selectField.name);
            if (c) {
                c.disable({ emitEvent: false });
                c.statusChanges.pipe(takeUntil(this.unsubscribe))
                    .subscribe(() => {
                        c.disable({ emitEvent: false });
                    });
            }
        }
    }

    ngOnDestroy() {
        this.unsubscribe.next();
        this.unsubscribe.complete();
    }

    save() {
        const confirmDialog = this.openConfirmDialog(this.transloco.translate('saveRowConfirm'));
        confirmDialog.afterClosed().subscribe((confirm) => {
            if (confirm === 'confirmed') {
                this.form.control.markAllAsTouched();
                if (!this.form.control.valid) {
                    this.dialogsService.openErrorDialog({
                        message: this.transloco.translate('errorsInForm')
                    });
                    return;
                }
                this.closeDialog(this.form);
            }
        });
    }

    closeDialog(result?: any): void {
        this.dialogRef.close(result);
    }

    private openConfirmDialog(message: string): MatDialogRef<FuseConfirmationDialogComponent, any> {
        return this._fuseConfirmationService.open({
            title: this.transloco.translate('warning'),
            message: message,
            actions: {
                confirm: {
                    label: this.transloco.translate('confirm')
                },
                cancel: {
                    label: this.transloco.translate('cancel')
                }
            }
        });
    }

}
